// Locating and marking a spelling suggestion's text inside rendered DOM.
//
// Extracted from DocxViewer so the Note of Presentation AI panel can highlight
// the same way against Jodit-rendered rich text. Both callers pass their own
// container element; nothing here is specific to .docx rendering.
//
// The class names keep the `docx-` prefix so the existing DocxViewer stylesheet
// and any saved user CSS continue to match.

const SPELL_FLAG_CLASS = 'docx-spell-flag';
const SPELL_FLAG_ACTIVE_CLASS = 'docx-spell-flag-active';

// Shared stylesheet for the marker spans, so every consumer highlights
// identically instead of re-declaring near-identical rules.
const SPELL_FLAG_CSS = `
.${SPELL_FLAG_CLASS} {
	background: rgba(255, 214, 0, 0.35);
	border-bottom: 2px solid #e6a700;
	border-radius: 2px;
	cursor: pointer;
}
.${SPELL_FLAG_CLASS}.${SPELL_FLAG_ACTIVE_CLASS} {
	background: rgba(255, 149, 0, 0.55);
	border-bottom-color: #cc6d00;
}
`;

function collapseWhitespace(str) {
	let collapsed = '';
	const map = [];
	let inWhitespace = false;
	for (let i = 0; i < str.length; i++) {
		const isWs = /\s/.test(str[i]);
		if (isWs) {
			if (!inWhitespace) {
				collapsed += ' ';
				map.push(i);
			}
			inWhitespace = true;
		} else {
			collapsed += str[i];
			map.push(i);
			inWhitespace = false;
		}
	}
	return { collapsed, map };
}

// Finds, in document order, the single text-node run of `container` whose
// concatenated text contains `context` — the AI-provided surrounding snippet
// — so a suggestion is anchored to one specific occurrence rather than every
// instance of a possibly-repeated word. Falls back to matching bare
// `original` if `context` isn't found, so a suggestion still marks
// *something* rather than silently disappearing — first occurrence only,
// since without context there's no way to disambiguate repeats.
function locateSuggestionRange(container, original, context) {
	if (!original) return null;
	const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);
	const nodes = [];
	let node;
	while ((node = walker.nextNode())) nodes.push(node);

	const fullText = nodes.map((n) => n.nodeValue).join('');
	const { collapsed: fullCollapsed, map: fullMap } = collapseWhitespace(fullText);
	const { collapsed: originalCollapsed } = collapseWhitespace(original);
	const { collapsed: contextCollapsed } = collapseWhitespace(context || '');

	const needle =
		contextCollapsed && fullCollapsed.includes(contextCollapsed) && contextCollapsed.includes(originalCollapsed) ? contextCollapsed : originalCollapsed;
	const needleStart = fullCollapsed.indexOf(needle);
	if (needleStart === -1) return null;
	const originalOffsetInNeedle = needle === originalCollapsed ? 0 : needle.indexOf(originalCollapsed);
	if (originalOffsetInNeedle === -1) return null;
	const collapsedTargetStart = needleStart + originalOffsetInNeedle;
	const collapsedTargetEnd = collapsedTargetStart + originalCollapsed.length;
	if (collapsedTargetEnd > fullCollapsed.length) return null;

	// Map collapsed-space offsets back to real offsets into fullText. The end
	// offset needs the position *after* the last matched character, which
	// isn't itself in the map when the match runs to the very end of fullText.
	const targetStart = fullMap[collapsedTargetStart];
	const targetEnd = collapsedTargetEnd < fullMap.length ? fullMap[collapsedTargetEnd] : fullText.length;
	if (targetStart == null || targetEnd == null || targetEnd <= targetStart) return null;

	let pos = 0;
	let startNode = null;
	let startOffset = 0;
	let endNode = null;
	let endOffset = 0;
	for (const n of nodes) {
		const len = n.nodeValue.length;
		if (!startNode && targetStart >= pos && targetStart < pos + len) {
			startNode = n;
			startOffset = targetStart - pos;
		}
		if (!endNode && targetEnd > pos && targetEnd <= pos + len) {
			endNode = n;
			endOffset = targetEnd - pos;
		}
		pos += len;
		if (startNode && endNode) break;
	}
	if (!startNode || !endNode) return null;
	// Same-node ranges must have end >= start, or downstream splitText() math
	// underflows into a huge unsigned offset instead of throwing cleanly.
	if (startNode === endNode && endOffset < startOffset) return null;

	const range = document.createRange();
	range.setStart(startNode, startOffset);
	range.setEnd(endNode, endOffset);
	return range;
}

// Wraps a Range in a marker <span> so later code can find/highlight/replace
// it by id — Range.surroundContents() throws if the range crosses element
// boundaries (a misspelling split across two <span> runs by a font/size
// change), so this walks the range's nodes and wraps each manually instead.
function wrapRangeInSpan(range, spanId) {
	if (range.startContainer === range.endContainer) {
		const textNode = range.startContainer;
		// Live Ranges auto-adjust their boundary offsets when the node they
		// reference is mutated (per the DOM spec) — splitText() on textNode
		// is exactly such a mutation, so range.startOffset/endOffset change
		// out from under this function the instant splitText() runs. Reading
		// them into locals first keeps the second splitText() call's math
		// (end - start) based on the original, stable offsets.
		const { startOffset, endOffset } = range;
		const span = document.createElement('span');
		span.className = SPELL_FLAG_CLASS;
		span.dataset.spellId = spanId;
		const middle = textNode.splitText(startOffset);
		middle.splitText(endOffset - startOffset);
		middle.parentNode.insertBefore(span, middle);
		span.appendChild(middle);
		return;
	}
	// Multi-node case: extract the range's contents (this correctly clones
	// partial start/end text nodes) and re-insert them wrapped in one span.
	const fragment = range.extractContents();
	const span = document.createElement('span');
	span.className = SPELL_FLAG_CLASS;
	span.dataset.spellId = spanId;
	span.appendChild(fragment);
	range.insertNode(span);
}

export { collapseWhitespace, locateSuggestionRange, wrapRangeInSpan, SPELL_FLAG_CLASS, SPELL_FLAG_ACTIVE_CLASS, SPELL_FLAG_CSS };
